import React, { useState, useEffect, useCallback } from "react";
import { generateTbChestReport } from "../../utils/tbreport";
import { API_BASE_URL } from "../Api/apiconnector";

export default function TbChestForm({ generateReport , currentUser }) {
  const [showForm, setShowForm] = useState(true); // ✅ show/hide form
  const [formData, setFormData] = useState({
    name: "",
    patientId: "",
    age: "",
    testDate: "",
    reportDate: "",
    gender: "",
    reportTime: "",
    referralDoctor: "",
    xrayType: "PA",
    normal: true,
    suspectedTB: false,
    zones: { RUZ: false, RMZ: false, RLZ: false, LUZ: false, LMZ: false, LLZ: false },
    notes: "",
  });

  useEffect(() => {
    const fetchDemographics = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const patientId = urlParams.get("id");
        if (!patientId) return;

        const response = await fetch(`${API_BASE_URL}/fetch-dicom/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ id: patientId }),
        });

        if (!response.ok) throw new Error(`Failed to fetch demographics (${response.status})`);

        const data = await response.json();
        console.log("Fetched demographics:", data);

        // ✅ Use study_date directly (already dd-mm-yyyy)
        const formattedStudyDate = data.study_date || "";

        // ✅ Get current date & time in dd-mm-yyyy and HH:mm:ss
        const now = new Date();
        const formattedReportDate = formatToDDMMYYYY(now);
        const formattedReportTime = now.toLocaleTimeString("en-GB"); // e.g. 14:45:22

        setFormData((prev) => ({
          ...prev,
          name: data.patient_name || "",
          patientId: data.patient_id || "",
          age: data.age || "",
          gender: data.gender || "",
          testDate: formattedStudyDate,
          reportDate: formattedReportDate,
          reportTime: formattedReportTime,
          referralDoctor: data.referring_doctor_name || "",
        }));
      } catch (error) {
        console.error("Error fetching DICOM demographics:", error);
      }
    };

    fetchDemographics();
  }, []);

  // ✅ Keep date in dd-mm-yyyy format
  function formatToDDMMYYYY(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Convert dd-mm-yyyy → yyyy-mm-dd for <input type="date">
  function formatToISODate(ddmmyyyy) {
    if (!ddmmyyyy) return "";
    const [day, month, year] = ddmmyyyy.split("-");
    return `${year}-${month}-${day}`;
  }

  // Convert yyyy-mm-dd → dd-mm-yyyy for storing
  function formatToDDMMYYYYFromISO(isoDate) {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}-${month}-${year}`;
  }
  

  const handleInputChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleZoneChange = (zone) =>
    setFormData((prev) => ({
      ...prev,
      zones: { ...prev.zones, [zone]: !prev.zones[zone] },
    }));

  // ✅ Hide form on Back click
  const handleBack = () => {
    setShowForm(false);
  };

  const handleDone = () => {
    const mappedData = mapFormDataToReportFormat(formData);
    

    const reportHtml = generateTbChestReport(mappedData, currentUser);

    // ✅ If CKEditor present, append report content
    if (window.editor) {
      const existingData = window.editor.getData();
      const updatedData = `${existingData}<br/><hr/><br/>${reportHtml}`;
      window.editor.setData(updatedData);
    }

    // Optional callback for parent
    if (generateReport) generateReport(reportHtml);

    // ✅ Hide form after Done
    setShowForm(false);
  };

  if (!showForm) return null; // ✅ Hide form after Done or Back

  return (
    <div className="max-w-2xl mx-auto p-4 bg-white rounded-xl shadow">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold">TbChest</h3>
        <div className="flex gap-2">
          {/* ✅ Back button now hides the form */}
          <button
            onClick={handleBack}
            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Back
          </button>
          <button
            onClick={handleDone}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Done
          </button>
          {/* ❌ Reject button removed */}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <input
          type="text"
          placeholder="Name"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          className="border p-2 rounded"
        />
        <input
          type="text"
          placeholder="Patient ID"
          value={formData.patientId}
          onChange={(e) => handleInputChange("patientId", e.target.value)}
          className="border p-2 rounded"
        />
        <input
          type="number"
          placeholder="Age"
          value={formData.age}
          onChange={(e) => handleInputChange("age", e.target.value)}
          className="border p-2 rounded"
        />
        <select
          value={formData.gender}
          onChange={(e) => handleInputChange("gender", e.target.value)}
          className="border p-2 rounded"
        >
          <option value="">Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Others">Others</option>
        </select>
        <input
          type="date"
          value={formData.testDate ? formatToISODate(formData.testDate) : ""}
          onChange={(e) =>
            handleInputChange("testDate", formatToDDMMYYYYFromISO(e.target.value))
          }
          className="border p-2 rounded"
        />

        <input
          type="date"
          value={formData.reportDate ? formatToISODate(formData.reportDate) : ""}
          onChange={(e) =>
            handleInputChange("reportDate", formatToDDMMYYYYFromISO(e.target.value))
          }
          className="border p-2 rounded"
        />
      </div>

      <div className="mb-3">
        <label className="font-medium mr-2">X-Ray Type*</label>
        <select
          value={formData.xrayType}
          onChange={(e) => handleInputChange("xrayType", e.target.value)}
          className="border p-2 rounded"
        >
          <option value="AP">AP</option>
          <option value="PA">PA</option>
          <option value="LATERAL">LATERAL</option>
          <option value="AP/PA">AP/PA</option>
        </select>
      </div>

      <div className="flex gap-6 mb-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.normal}
            onChange={(e) => handleInputChange("normal", e.target.checked)}
          />
          Normal?
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.suspectedTB}
            onChange={(e) => handleInputChange("suspectedTB", e.target.checked)}
          />
          Suspected TB?
        </label>
      </div>

      {formData.suspectedTB && (
        <div className="border-t pt-2 mb-3">
          <h4 className="font-medium mb-2">TB Zones:</h4>
          <div className="grid grid-cols-3 gap-2">
            {Object.keys(formData.zones).map((zone) => (
              <label key={zone} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.zones[zone]}
                  onChange={() => handleZoneChange(zone)}
                />
                {zone}
              </label>
            ))}
          </div>
        </div>
      )}

      <textarea
        placeholder="Additional Notes"
        value={formData.notes}
        onChange={(e) => handleInputChange("notes", e.target.value)}
        className="border p-2 rounded w-full h-20 mb-4"
      />
    </div>
  );
}

// ✅ Helper mapping function (unchanged)
const mapFormDataToReportFormat = (formData) => ({
  NameTextFR2: formData.name,
  IDTextFR2: formData.patientId,
  AgeTextFR2: formData.age,
  GenderTextFR2: formData.gender,
  TestDateTextFR2: formData.testDate,
  ReportDateTextFR2: formData.reportDate,
  ReportTimeTextFR2: formData.reportTime,
  ReferralDoctorTextFR2: formData.referralDoctor,
  XrayTypes: formData.xrayType,
  BothNormal: formData.normal,
  suspectedTB: formData.suspectedTB,
  suspectedTBType: Object.keys(formData.zones).filter((z) => formData.zones[z]),
  AdditionalNotes: formData.notes,
});
